import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Lock, ArrowRight, Loader2, Tag, Check } from "lucide-react";
import { PACKAGES } from "@/constants/testIds";
import { PayPalButton } from "./PayPalButton";
import { ConsentCheckbox } from "./ConsentCheckbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NZD_BY_AUD = { 207: 249, 1240: 1495, 2900: 3495, 4975: 5995, 6635: 7995 };

export const PackageCheckoutModal = ({ pkg, onClose }) => {
  const [firstName, setFirstName] = useState(pkg?.prefill?.first_name || "");
  const [lastName, setLastName] = useState(pkg?.prefill?.last_name || "");
  const [email, setEmail] = useState(pkg?.prefill?.email || "");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState("");
  const [discount, setDiscount] = useState(null); // {percent_off, code}
  const [codeError, setCodeError] = useState("");
  const [validatingCode, setValidatingCode] = useState(false);

  // Auto-prefill code from URL ?code=XXX
  useEffect(() => {
    const url = new URLSearchParams(window.location.search);
    const c = url.get("code");
    if (c) setCode(c.toUpperCase());
  }, []);

  const applyCode = async () => {
    const trimmed = code.trim();
    if (!trimmed) {
      setDiscount(null);
      setCodeError("");
      return;
    }
    setValidatingCode(true);
    setCodeError("");
    try {
      const { data } = await axios.post(`${API}/discounts/validate`, {
        code: trimmed,
        scope: "packages",
        email: email.trim(),
        product_id: pkg.id,
      });
      setDiscount({ code: data.code, percent_off: data.percent_off });
      toast.success(`${data.percent_off}% off applied`);
    } catch (err) {
      setDiscount(null);
      setCodeError(err.response?.data?.detail || "Invalid code");
    } finally {
      setValidatingCode(false);
    }
  };

  const finalPrice = discount
    ? Math.round(pkg.price * (1 - discount.percent_off / 100) * 100) / 100
    : pkg.price;

  const submit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim() || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Please add your full name and a valid email.");
      return;
    }
    if (!consent) {
      toast.error("Please tick the acknowledgement to continue.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/packages/checkout`, {
        package_id: pkg.id,
        email: email.trim(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        origin_url: window.location.origin,
        discount_code: discount?.code || null,
      });
      window.location.href = data.url;
    } catch (err) {
      setLoading(false);
      toast.error("Couldn't start checkout — please try again.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[var(--ink)]/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid={PACKAGES.modal}
    >
      <div
        className="w-full max-w-md bg-[var(--paper)] rounded-3xl border border-[var(--line)] p-7 md:p-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <span className="num-tag text-xs">Relocation package</span>
            <h3 className="font-display text-2xl font-extrabold mt-2 leading-tight">{pkg.name}</h3>
            <p className="font-display font-bold text-lg text-[var(--ink)] mt-1" data-testid={PACKAGES.price}>
              AU${pkg.price.toLocaleString()}
              {NZD_BY_AUD[pkg.price] && (
                <span className="text-sm text-[var(--ocean)] font-normal ml-2">
                  / NZ${NZD_BY_AUD[pkg.price].toLocaleString()}
                </span>
              )}
            </p>
          </div>
          <button
            type="button"
            data-testid={PACKAGES.close}
            onClick={onClose}
            className="shrink-0 p-2 rounded-full hover:bg-[var(--mist-soft)] transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-widest font-bold text-[var(--ocean)] mb-1.5">
                First name
              </label>
              <input
                data-testid={PACKAGES.firstName}
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name"
                className="w-full rounded-xl border border-[var(--line)] bg-white px-4 py-3 text-base focus:outline-none focus:border-[var(--ink)] transition-colors"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest font-bold text-[var(--ocean)] mb-1.5">
                Last name
              </label>
              <input
                data-testid={PACKAGES.lastName}
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last name"
                className="w-full rounded-xl border border-[var(--line)] bg-white px-4 py-3 text-base focus:outline-none focus:border-[var(--ink)] transition-colors"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest font-bold text-[var(--ocean)] mb-1.5">
              Email — your client agreement lands here
            </label>
            <input
              data-testid={PACKAGES.email}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className="w-full rounded-xl border border-[var(--line)] bg-white px-4 py-3 text-base focus:outline-none focus:border-[var(--ink)] transition-colors"
            />
          </div>

          <ConsentCheckbox checked={consent} onChange={setConsent} testId="package-consent" />

          {/* Discount code */}
          <div data-testid="package-discount-block">
            <label className="block text-xs uppercase tracking-widest font-bold text-[var(--ocean)] mb-1.5 flex items-center gap-1.5">
              <Tag size={11} /> Have a code?
            </label>
            <div className="flex gap-2">
              <input
                data-testid="package-discount-code"
                type="text"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.toUpperCase());
                  setDiscount(null);
                  setCodeError("");
                }}
                placeholder="Optional"
                className="flex-1 rounded-xl border border-[var(--line)] bg-white px-4 py-2.5 text-sm font-mono uppercase focus:outline-none focus:border-[var(--ink)] transition-colors"
              />
              <button
                type="button"
                data-testid="package-discount-apply"
                onClick={applyCode}
                disabled={!code.trim() || validatingCode || !!discount}
                className="px-4 py-2.5 text-xs uppercase tracking-widest font-bold rounded-xl border border-[var(--line)] hover:border-[var(--ink)] transition-colors disabled:opacity-50"
              >
                {validatingCode ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : discount ? (
                  <Check size={14} />
                ) : (
                  "Apply"
                )}
              </button>
            </div>
            {discount && (
              <p className="mt-2 flex items-center justify-between text-xs">
                <span className="inline-flex items-center gap-1.5 text-[var(--lime-deep)] font-bold">
                  <Check size={12} /> {discount.code} — {discount.percent_off}% off
                </span>
                <span className="font-mono text-[var(--ink)]">
                  AU$<span className="line-through text-[var(--ocean)]/60 mr-1">{pkg.price.toLocaleString()}</span>
                  <span className="font-bold">{finalPrice.toLocaleString()}</span>
                </span>
              </p>
            )}
            {codeError && (
              <p className="mt-2 text-xs text-red-600" data-testid="package-discount-error">{codeError}</p>
            )}
          </div>

          <button
            type="submit"
            data-testid={PACKAGES.submit}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-[var(--ink)] text-[var(--paper)] font-display font-bold rounded-xl px-6 py-3.5 hover:bg-[var(--ink-soft)] transition-colors disabled:opacity-60"
          >
            {loading ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <>
                Continue to secure checkout <ArrowRight size={16} />
              </>
            )}
          </button>
        </form>

        <PayPalButton
          kind="package"
          email={email}
          firstName={firstName}
          lastName={lastName}
          packageId={pkg.id}
          discountCode={discount?.code || null}
          disabled={
            !firstName.trim() || !lastName.trim() || !/^\S+@\S+\.\S+$/.test(email) || !consent || loading
          }
          onSuccess={() => {
            window.location.href = `/packages/confirmation?provider=paypal&package=${pkg.id}`;
          }}
        />

        <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-[var(--ocean)]">
          <Lock size={12} /> Secure checkout by Stripe or PayPal · The team is in touch within 48 hours
        </p>
      </div>
    </div>
  );
};
